// 共通ログイン画面（external-design/portal.md「共通ログイン画面」/ login.md S1）。
//
// OAuth の authorize 要求（client_id / redirect_uri / state / code_challenge …）を受け、email の
// ドメインで振り分け先を決めて、同じパラメータをそのまま Cognito の /oauth2/authorize へ転送する。
// 同じタブでポータルにログイン中なら、サービスの要求でも email を聞かずにその email で振り分ける（ADR-0021）。
// 要求パラメータ無しで開かれたら（サービス・ポータルのログアウト後の着地、招待メールのリンク、直接開いた）、
// ポータル自身のセッションを終えてから、ポータル自身のログインを開始する（ADR-0026。ライブラリがこの画面へ戻る）。

import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { getConfig } from "../lib/config";
import { currentUser, endSession, signIn } from "../lib/auth";
import { EMAIL_RE, REMEMBER_KEY, buildAuthorizeUrl, emailDomainOf, emailToForward, parseAuthorizeRequest, portalInvitedContract, portalLoginHint } from "../lib/login";

const remembered = () => { try { return localStorage.getItem(REMEMBER_KEY); } catch { return null; } };

export function LoginPage() {
  const req = useMemo(() => parseAuthorizeRequest(window.location.search), []);
  // 入力を飛ばして振り分ける email。undefined は判定中（ポータルのセッションを読んでいる）、null は入力させる。
  const [auto, setAuto] = useState<string | null | undefined>(undefined);
  const [email, setEmail] = useState(req?.loginHint ?? remembered() ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const started = useRef(false);

  /** email のドメインで振り分け先を引き、受け取った authorize 要求を Cognito へ転送する。 */
  const forward = async (raw: string) => {
    if (!req) return;
    const v = raw.trim().toLowerCase();
    if (!EMAIL_RE.test(v)) { setError("メールアドレスの形式が正しくありません。"); return; }
    setBusy(true); setError(null);
    try { localStorage.setItem(REMEMBER_KEY, v); } catch { /* 記憶できなくても続ける */ }
    const domain = emailDomainOf(v)!;
    // 到達不能・障害時はパスワード導線へフォールバックする（api.md「到達不能時」）。
    const lookup = await api.idpLookup(domain).catch(() => null);
    // login_hint は Cognito が無視することがあるが、パスワード画面の既定値として渡す。
    req.params.set("login_hint", v);
    window.location.assign(buildAuthorizeUrl(req, lookup, getConfig().cognitoDomain));
  };

  useEffect(() => {
    // authorize 要求が無い = ログアウト後の着地（サービスとポータルの logout_uri）・招待メールのリンク・直接開いた。
    // どれもポータル自身のセッションを終えてから、ポータル自身のログインを始める。これで ADR-0021 の
    // 「ログイン中なら聞かない」はログアウト後には効かず、メールアドレスの入力画面が出る（ADR-0026）。
    // パスワードの招待メールのリンクなら login_hint を引き継ぎ、メールアドレスの入力を飛ばす。
    // 招待した契約（contract）があれば再認証を求め、ログイン後に参加した契約を示す。
    if (started.current) return; // 開発時の StrictMode で effect が 2 回走っても 1 回だけ遷移する
    if (!req) {
      started.current = true;
      const hint = portalLoginHint(window.location.search);
      const invitedContractId = portalInvitedContract(window.location.search);
      void endSession().catch(() => undefined).then(() => signIn(hint, { invitedContractId }));
      return;
    }
    // email が分かっていれば（招待の受諾など、または同じタブでポータルにログイン中）、入力画面を出さずに振り分ける。
    // セッションは読めなくても（壊れた保存など）入力画面を出して止めない。
    let active = true;
    currentUser().catch(() => null).then((user) => {
      if (!active || started.current) return;
      const email = emailToForward(req, getConfig().clientId, user?.profile.email);
      setAuto(email);
      if (email) { started.current = true; void forward(email); }
    });
    return () => { active = false; };
  }, [req]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!req || auto !== null) return <main className="narrow"><p className="muted">ログインを開始しています…</p></main>;

  const submit = (e: React.FormEvent) => { e.preventDefault(); void forward(email); };

  return (
    <div className="login">
      <div className="login-logo">Cifra</div>
      <h1 className="login-title">シフラアカウントにログイン</h1>
      <form className="login-card" onSubmit={submit}>
        <label htmlFor="email">メールアドレス</label>
        <input id="email" type="email" autoComplete="username" autoFocus placeholder="email@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        {error && <p className="error">{error}</p>}
        <button className="primary" type="submit" disabled={busy}>{busy ? "確認中…" : "次へ"}</button>
        <p className="muted">組織の SSO が登録されていれば SSO へ、なければパスワード入力へ進みます。</p>
      </form>
    </div>
  );
}
